import viewerStyles from './block-viewer.module.css'
import { inlineGlossClassName } from './language-span'
import {
  FormatFlag,
  diffMarker,
  isDiffCodeBlock,
  resolvePageRef,
  getTextFromChars,
  type Block,
  type Blocks,
  type CommentThread,
  type PageMeta,
  type PageRef,
  type QuestionBlock,
} from './block'
import { AudioPlayer } from '@w/react-audio'
import {
  CodeHighlighting,
  CodeHighlightingControls,
  codeHighlightingSurfaceClassName,
} from '@w/code-highlighting'
import { Alert } from '@w/ui/components/alert'
import { Avatar } from '@w/ui/components/avatar'
import { sidebarMenuButtonVariants } from '@w/ui/lib/sidebar-menu-button'
import { Button } from '@w/ui/components/button'
import { Checkbox } from '@w/ui/components/checkbox'
import { ChoiceField } from '@w/ui/components/choice-field'
import { Chip, type ChipTagColor } from '@w/ui/components/chip'
import {
  Dialog,
  DialogClose,
  DialogPopup,
  DialogTitle,
  DialogTrigger,
} from '@w/ui/components/dialog'
import {
  DownloadIcon,
  FileTextIcon,
  InfoIcon,
  CircleCheckIcon,
  CircleAlertIcon,
  TriangleAlertIcon,
  ExitFullscreenIcon,
  FullscreenIcon,
  MoreHorizontalIcon,
} from '@w/ui/components/icon'
import { IconButton } from '@w/ui/components/icon-button'
import { Image } from '@w/ui/components/image'
import { InputField } from '@w/ui/components/input-field'
import { Separator } from '@w/ui/components/separator'
import { Table, TableBody, TableCell, TableRow } from '@w/ui/components/table'
import { Text } from '@w/ui/components/text'
import { YoutubeEmbed } from '@w/ui/components/youtube-embed'
import { cn } from '@w/ui/lib/utils'
import {
  relativeColorClasses,
  relativeColorName,
} from '@w/ui/lib/relative-color'
import { MathFormula } from './math'
import type {
  AnchorHTMLAttributes,
  ComponentType,
  CSSProperties,
  ReactNode,
} from 'react'
import {
  Children,
  cloneElement,
  Fragment,
  isValidElement,
  lazy,
  Suspense,
} from 'react'
import type { MarkdownSourceMap, MarkdownSourceRange } from './types'
import {
  reviewPresentationClassName,
  reviewPresentationDataAttributes,
  type ReviewPresentationDataAttributes,
} from './review-presentation'

// Relative lazy import keeps the package graph acyclic when whiteboard uses
// InlineBlockViewer for portals.
const WhiteboardGraphViewer = lazy(() =>
  import('../../graph-viewer/src/index').then((module) => ({
    default: module.WhiteboardGraphViewer,
  })),
)

export interface BlockViewerLinkProps extends Omit<
  AnchorHTMLAttributes<HTMLAnchorElement>,
  'href'
> {
  href: string
}

export interface BlockViewerProps {
  Link?: ComponentType<BlockViewerLinkProps>
  blocks: Blocks
  comments?: readonly CommentThread[]
  className?: string
  empty?: ReactNode
  reviewMode?: boolean
  rootDecoration?: BlockViewerRootDecoration
  resolveFileUrl?: (src: string) => string
  spaceId: string
  resolvePage?: (ref: PageRef) => BlockViewerPageInfo
}

export interface BlockViewerRootDecoration {
  render: (props: BlockViewerRootDecorationProps) => ReactNode
  sourceMap?: MarkdownSourceMap
}

export interface BlockViewerRootDecorationProps {
  block: Block
  children: ReactNode
  index: number
  sourceRange?: MarkdownSourceRange
}

export interface BlockViewerPageInfo {
  emoji?: string
  label: string
}

interface RenderContext {
  Link: ComponentType<BlockViewerLinkProps>
  resolveFileUrl: (src: string) => string
  spaceId: string
  resolvePage: (ref: PageRef) => BlockViewerPageInfo
  reviewMode: boolean
  commentsById: ReadonlyMap<string, CommentThread>
}

export function BlockViewer({
  Link = AnchorLink,
  blocks,
  comments = [],
  className,
  empty = null,
  reviewMode = false,
  rootDecoration,
  resolveFileUrl = identity,
  resolvePage = defaultPageInfo,
  spaceId,
}: BlockViewerProps) {
  if (blocks.length === 0) return empty
  const pageHeader = findPageHeader(blocks)
  const pageMeta = pageHeader?.meta
  const context = {
    Link,
    resolveFileUrl,
    resolvePage,
    spaceId,
    reviewMode,
    commentsById: new Map(comments.map((thread) => [thread.threadId, thread])),
  }

  return (
    <article
      className={cn(
        'text-on-surface text-body-medium mx-auto w-full max-w-[700px] min-w-0 overflow-x-clip leading-relaxed break-words',
        className,
      )}
      data-content-block-viewer=""
    >
      {pageHeader &&
        decorateRootBlock(
          pageHeader.root,
          pageHeader.index,
          pageHeader.root.type === 'review' && reviewMode ? (
            <ReviewContent block={pageHeader.root}>
              <PageHeader
                meta={pageHeader.meta}
                resolveFileUrl={resolveFileUrl}
              />
            </ReviewContent>
          ) : (
            <PageHeader
              meta={pageHeader.meta}
              resolveFileUrl={resolveFileUrl}
            />
          ),
          rootDecoration,
        )}
      <div className={cn(pageMeta && hasPageHeaderContent(pageMeta) && 'px-6 pb-8')}>
        {renderRootBlocks(blocks, context, rootDecoration)}
      </div>
    </article>
  )
}

export function InlineBlockViewer({
  Link = AnchorLink,
  blocks,
  comments = [],
  reviewMode = false,
  resolveFileUrl = identity,
  resolvePage = defaultPageInfo,
  spaceId,
}: Omit<BlockViewerProps, 'className' | 'empty' | 'rootDecoration'>) {
  return renderBlocks(blocks, {
    Link,
    resolveFileUrl,
    resolvePage,
    spaceId,
    reviewMode,
    commentsById: new Map(comments.map((thread) => [thread.threadId, thread])),
  })
}

function renderRootBlocks(
  items: Blocks,
  context: RenderContext,
  rootDecoration: BlockViewerRootDecoration | undefined,
): ReactNode {
  return items.map((block, index) => {
    if (isPageHeaderRoot(block)) return null
    const content = renderBlock(block, context)
    return rootDecoration
      ? decorateRootBlock(block, index, content, rootDecoration)
      : content
  })
}

function findPageHeader(blocks: Blocks): {
  root: PageMeta | Extract<Block, { type: 'review' }>
  meta: PageMeta
  index: number
} | null {
  for (const [index, block] of blocks.entries()) {
    if (block.type === 'pageMeta') return { root: block, meta: block, index }
    const meta = reviewedPageMeta(block)
    if (meta && block.type === 'review') return { root: block, meta, index }
  }
  return null
}

function isPageHeaderRoot(block: Block) {
  return block.type === 'pageMeta' || Boolean(reviewedPageMeta(block))
}

function reviewedPageMeta(block: Block): PageMeta | null {
  if (
    block.type !== 'review' ||
    block.display !== 'block' ||
    block.children.length !== 1
  ) {
    return null
  }
  const child = block.children[0]
  return child?.type === 'pageMeta' ? child : null
}

function decorateRootBlock(
  block: Block,
  index: number,
  children: ReactNode,
  rootDecoration: BlockViewerRootDecoration | undefined,
): ReactNode {
  if (!rootDecoration) return children
  return (
    <Fragment key={`${block.id}:${index}`}>
      {rootDecoration.render({
        block,
        children,
        index,
        sourceRange: rootDecoration.sourceMap?.get(block.id),
      })}
    </Fragment>
  )
}

function renderBlocks(items: Blocks, context: RenderContext): ReactNode {
  return items.map((block) => renderBlock(block, context))
}

function renderCaption(items: Blocks, context: RenderContext): ReactNode {
  return items.map((block) =>
    block.type === 'p'
      ? renderBlocks(block.children, context)
      : renderBlock(block, context),
  )
}

function renderListItemContent(
  items: Blocks,
  context: RenderContext,
): ReactNode {
  return items.map((block) =>
    block.type === 'p' ? (
      <div className="contents" data-content-block-id={block.id} key={block.id}>
        {renderBlocks(block.children, context)}
      </div>
    ) : (
      renderBlock(block, context)
    ),
  )
}

function renderBlock(block: Block, context: RenderContext): ReactNode {
  const children = 'children' in block ? block.children : undefined
  const content = children ? renderBlocks(children, context) : null

  switch (block.type) {
    case 'text':
      return block.text
    case 'chars':
      return <span key={block.id}>{renderChars(block)}</span>
    case 'br':
      return <br key={block.id} />
    case 'h': {
      const Heading = block.level === 1 ? 'h1' : block.level === 2 ? 'h2' : 'h3'
      return (
        <Heading
          className={cn(
            'mt-8 mb-3 leading-tight font-medium text-balance first:mt-0',
            block.level === 1 && 'text-header-big',
            block.level === 2 && 'text-header-medium',
            block.level === 3 && 'text-header-small',
          )}
          data-content-block-id={block.id}
          key={block.id}
        >
          {content}
        </Heading>
      )
    }
    case 'p':
      return isPageNavigationParagraph(block) ? (
        <div
          className="min-w-0"
          data-content-block-id={block.id}
          key={block.id}
        >
          {content}
        </div>
      ) : hasBlockChildren(block.children) ? (
        <div
          className="my-3 min-w-0"
          data-content-block-id={block.id}
          key={block.id}
        >
          {content}
        </div>
      ) : (
        <Text
          className={cn(
            'my-3',
            block.align === 'center' && 'w-full text-center',
            block.align === 'right' && 'w-full text-right',
          )}
          data-content-block-id={block.id}
          key={block.id}
          size="body"
        >
          {content}
        </Text>
      )
    case 'quote':
      return (
        <figure
          className="border-primary my-5 border-l-4 py-1 ps-4"
          data-content-block-id={block.id}
          key={block.id}
        >
          <blockquote className="m-0">{content}</blockquote>
          {block.author && (
            <Text
              className="mt-2 text-right"
              render={<figcaption />}
              size="label"
              tone="muted"
            >
              — {renderCaption(block.author, context)}
            </Text>
          )}
        </figure>
      )
    case 'list': {
      if (block.subtype === 'toggle') {
        return (
          <div className="border-separator my-3 border-l-2 ps-4" key={block.id}>
            {content}
          </div>
        )
      }
      const List = block.subtype === 'ordered' ? 'ol' : 'ul'
      return (
        <List
          className={cn(
            'my-3',
            block.subtype === 'todo'
              ? 'list-none ps-2 [&_ol]:-ms-0.5 [&_ol]:ps-0 [&_ul]:-ms-0.5 [&_ul]:ps-0'
              : block.subtype === 'ordered'
                ? 'marker:text-primary list-decimal ps-6'
                : 'marker:text-primary list-disc ps-6',
          )}
          data-content-block-id={block.id}
          key={block.id}
        >
          {content}
        </List>
      )
    }
    case 'li':
      return (
        <li
          className={cn(
            'my-1 [&_ol]:my-1 [&_ul]:my-1',
            block.done !== undefined && 'flex items-start gap-3',
          )}
          data-content-block-id={block.id}
          key={block.id}
        >
          {block.done !== undefined && (
            <Checkbox
              aria-label={block.done ? 'Completed' : 'Not completed'}
              checked={block.done}
              className="shrink-0"
              disabled
            />
          )}
          <div className="min-w-0">
            {renderListItemContent(block.children, context)}
          </div>
        </li>
      )
    case 'a': {
      const Link = context.Link
      return (
        <Link
          className="text-primary underline decoration-current/30 underline-offset-3 hover:decoration-current"
          href={block.href}
          key={block.id}
          rel="noreferrer"
          target="_blank"
        >
          {content}
        </Link>
      )
    }
    case 'pre': {
      if (block.latex) {
        return (
          <MathFormula
            blockId={block.id}
            displayMode
            key={block.id}
            source={getPlainText(block.children)}
          />
        )
      }
      const code = block.lang?.toLowerCase() === 'graph' ? (
        <GraphCodeBlock
          blockId={block.id}
          code={getCodeSource(block)}
          key={block.id}
        />
      ) : block.lang?.toLowerCase() === 'mermaid' ? (
        <Suspense key={block.id} fallback={null}>
          <MermaidDiagram blockId={block.id} code={getCodeSource(block)} />
        </Suspense>
      ) : isDiffCodeBlock(block) ? (
        <DiffCodeBlock block={block} context={context} key={block.id} />
      ) : (
        <CodeHighlighting
          allowLanguageSelection={false}
          className="my-5 max-w-full"
          code={getPlainText(block.children)}
          data-content-block-id={block.id}
          key={block.id}
          language={block.lang ?? 'text'}
        />
      )
      if (!block.caption) return code
      // Block-level code keeps its my-5; -mt-3 collapses it to the media caption gap.
      return (
        <figure key={block.id}>
          {code}
          <Text
            className="-mt-3 mb-5 text-center"
            render={<figcaption />}
            size="label"
            tone="muted"
          >
            {renderCaption(block.caption, context)}
          </Text>
        </figure>
      )
    }
    case 'code':
      return (
        <MathFormula
          displayMode={false}
          key={block.id}
          source={getPlainText(block.children)}
        />
      )
    case 'hr':
      return (
        <Separator
          className="my-8"
          data-content-block-id={block.id}
          key={block.id}
        />
      )
    case 'l':
      return block.$gloss ? (
        <Fragment key={block.id}><span lang={block.lang}>{content}</span><span className={inlineGlossClassName}>{` (${block.$gloss})`}</span></Fragment>
      ) : (
        <span key={block.id} lang={block.lang}>{content}</span>
      )
    case 'em':
      return (
        <span
          className={emphasisColorClass(block.color)}
          data-color={block.color}
          key={block.id}
        >
          {content}
        </span>
      )
    case 'comment':
      return (
        <span
          className="bg-warning/10 decoration-warning rounded-sm underline decoration-2 underline-offset-2"
          data-comment-id={block.commentId}
          data-comment-status={
            context.commentsById.get(block.commentId)?.status
          }
          key={block.id}
        >
          {content}
        </span>
      )
    case 'definition':
    case 'criticComment':
    case 'comments':
      return null
    case 'diff':
      return (
        <DiffContent block={block} key={block.id}>
          {content}
        </DiffContent>
      )
    case 'review':
      return context.reviewMode ? (
        <ReviewContent block={block} key={block.id}>
          {content}
        </ReviewContent>
      ) : (
        content
      )
    case 'alert':
      return (
        <Alert
          className="text-body-medium my-5"
          data-content-block-id={block.id}
          icon={alertIcon(block.color)}
          key={block.id}
          title={block.title}
          type={block.color}
        >
          {content}
        </Alert>
      )
    case 'rule':
      return (
        <section
          className="my-5"
          data-content-block-id={block.id}
          data-rule-key={block.key}
          data-rule-scope={block.scope}
          key={block.id}
        >
          <div className="text-on-surface-secondary text-xs">{block.key}</div>
          {content}
        </section>
      )
    case 'questionGroup':
      // No frame around the group: the owner rejects callouts around cards; it groups content, it does not style it.
      return (
        <section data-content-block-id={block.id} data-question-group key={block.id}>
          {content}
        </section>
      )
    case 'table':
      return (
        <div
          className="my-5 max-w-full min-w-0"
          data-content-block-id={block.id}
          key={block.id}
        >
          <Table>
            <TableBody>{content}</TableBody>
          </Table>
        </div>
      )
    case 'tr':
      return (
        <TableRow data-content-block-id={block.id} key={block.id}>
          {content}
        </TableRow>
      )
    case 'td':
      return (
        <TableCell
          className="min-w-28 whitespace-normal"
          rowSpan={block.rowSpan}
          colSpan={block.colSpan}
          data-content-block-id={block.id}
          key={block.id}
        >
          {content}
        </TableCell>
      )
    case 'img':
      return (
        <figure
          className="mx-auto my-6 w-full max-w-full sm:w-(--content-block-media-width)"
          data-content-block-id={block.id}
          key={block.id}
          style={mediaWidthStyle(block.width)}
        >
          {/* Own aspect ratio, never cropped: a tall image is capped in height and centred, as Notion shows it. */}
          <Image
            className="w-full rounded-2xl [&>img]:mx-auto [&>img]:max-h-[min(70vh,40rem)] [&>img]:w-auto [&>img]:max-w-full [&>img]:rounded-2xl"
            alt={getPlainText(block.children ?? [])}
            intrinsic
            noThumb
            src={context.resolveFileUrl(block.src)}
          />
          {block.caption && (
            <Text
              className="mt-2 text-center"
              render={<figcaption />}
              size="label"
              tone="muted"
            >
              {renderCaption(block.caption, context)}
            </Text>
          )}
        </figure>
      )
    case 'video':
      return (
        <figure
          className="mx-auto my-6 w-full max-w-full sm:w-(--content-block-media-width)"
          data-content-block-id={block.id}
          key={block.id}
          style={mediaWidthStyle(block.width)}
        >
          <video
            autoPlay={block.autoplay}
            className="aspect-video w-full rounded-2xl object-cover"
            controls
            loop={block.autoplay}
            muted={block.autoplay}
            preload="metadata"
            src={context.resolveFileUrl(block.src)}
          />
          {block.caption && (
            <Text
              className="mt-2 text-center"
              render={<figcaption />}
              size="label"
              tone="muted"
            >
              {renderCaption(block.caption, context)}
            </Text>
          )}
        </figure>
      )
    case 'audio':
      return (
        <AudioPlayer
          className="my-5 max-w-md"
          data-content-block-id={block.id}
          fullWidth
          key={block.id}
          minimalBelowMs={5_000}
          autoplay={block.autoplay}
          src={context.resolveFileUrl(block.src)}
        />
      )
    case 'file':
      return (
        <a
          className="border-border text-on-surface my-5 flex max-w-md items-center gap-3 rounded-2xl border p-4 no-underline"
          data-content-block-id={block.id}
          href={context.resolveFileUrl(block.src)}
          key={block.id}
        >
          <DownloadIcon className="size-5 shrink-0" />
          <span className="min-w-0">
            <strong>{block.name}</strong>
            {block.size > 0 && (
              <Text render={<small />} size="hint" tone="muted">
                {formatBytes(block.size)}
              </Text>
            )}
          </span>
        </a>
      )
    case 'preview': {
      const youtubeId = getYoutubeId(block.src)
      return youtubeId ? (
        <figure
          className="mx-auto my-6 w-full max-w-full sm:w-(--content-block-media-width)"
          data-content-block-id={block.id}
          key={block.id}
          style={mediaWidthStyle(block.width)}
        >
          <YoutubeEmbed
            id={youtubeId}
            title={getPlainText(block.caption ?? []) || 'YouTube video'}
          />
          {block.caption && (
            <Text
              className="mt-2 text-center"
              render={<figcaption />}
              size="label"
              tone="muted"
            >
              {renderCaption(block.caption, context)}
            </Text>
          )}
        </figure>
      ) : (
        <a
          className="text-primary underline"
          data-content-block-id={block.id}
          href={block.src}
          key={block.id}
          rel="noreferrer"
          target="_blank"
        >
          {block.src}
        </a>
      )
    }
    case 'cols':
      return (
        <div
          className={cn(
            'min-w-0',
            isPageNavigationColumns(block)
              ? 'mt-8 mb-0 grid grid-cols-[repeat(2,50%)] gap-x-5 gap-y-4 sm:grid-flow-col sm:grid-cols-[repeat(3,33%)] sm:grid-rows-2 [&>section]:contents'
              : 'my-5 flex flex-col items-start gap-5 sm:flex-row',
          )}
          data-content-block-id={block.id}
          key={block.id}
        >
          {content}
        </div>
      )
    case 'col':
      return (
        <section
          data-content-block-id={block.id}
          key={block.id}
          className="w-full max-w-full min-w-0 sm:flex-[0_1_var(--content-block-column-width)]"
          style={
            {
              '--content-block-column-width': normalizeWidth(block.width),
            } as CSSProperties
          }
        >
          {content}
        </section>
      )
    case 'pageTitle':
      return (
        <h1 data-content-block-id={block.id} key={block.id}>
          {block.title}
        </h1>
      )
    case 'page':
      return (
        <PageLink
          pageBlock
          blockId={block.id}
          context={context}
          id={block.pageId}
          key={block.id}
        />
      )
    case 'pageLink':
      return (
        <PageLink
          blockId={block.id}
          context={context}
          id={block.pageId}
          spaceId={block.spaceId}
          key={block.id}
        />
      )
    case 'pages':
      return (
        <span key={block.id}>
          {block.ids.map((id) => (
            <a href={`#${id}`} key={id}>
              {id}
            </a>
          ))}
        </span>
      )
    case 'mention':
      return (
        <span
          className="bg-surface-2 text-primary rounded-md px-1.5 py-0.5"
          key={block.id}
        >
          {mentionLabel(block)}
        </span>
      )
    case 'root':
      return (
        <div data-content-block-id={block.id} key={block.id}>
          {content}
        </div>
      )
    case 'cloze':
      return (
        <Fragment key={block.id}>
          {block.$showAnswer && (
            <ClozeAnswerAudio block={block} resolve={context.resolveFileUrl} />
          )}
          <Text
            className="bg-surface-2 mx-0.5 rounded-lg px-1.5 py-0.5"
            inline
            render={<span />}
          >
            {content}
          </Text>
        </Fragment>
      )
    case 'question':
      return renderQuestion(block, context)
    case 'pageMeta':
      return null
  }
  return null
}

/**
 * BlockViewer is a static/article-mode renderer only (no dispatch, no
 * editing), so every quiz subtype below mirrors unni's `context === 'article'`
 * branch: the CORRECT answer is shown pre-filled, disabled, and styled as
 * success feedback.
 */
function renderQuestion(
  block: QuestionBlock,
  context: RenderContext,
): ReactNode {
  const question = block.question && (
    <div className="mb-3 font-medium" key="question">
      {renderBlocks(block.question, context)}
    </div>
  )

  switch (block.subtype) {
    case 'sa': {
      const answer = block.answer.join(', ')
      const explanation = block.explanation
        ? getPlainText(block.explanation)
        : undefined

      return block.inline ? (
        // InputField's own `className` lands on the inner control, so the Field
        // wrapper keeps its `flex-col` and would break the line; the wrapping
        // span re-inlines it via `[&>*]`.
        <span
          className="[&>*]:inline-flex [&>*]:align-middle"
          data-content-block-id={block.id}
          key={block.id}
        >
          {block.question && renderBlocks(block.question, context)}
          <InputField
            className="mx-1 inline-flex w-auto min-w-24 align-middle"
            disabled
            minimal
            state="success"
            value={answer}
          />
        </span>
      ) : (
        <div className="my-5" data-content-block-id={block.id} key={block.id}>
          {question}
          <InputField
            className="max-w-100"
            disabled
            state="success"
            tip={explanation}
            value={answer}
          />
        </div>
      )
    }
    case 'la': {
      // Unlike `sa`, an `la` block's `explanation` is optional model-answer
      // guidance. Rendering an empty disabled input would look like a blank
      // model answer, so the field is only shown when there's real text.
      const explanation = block.explanation
        ? getPlainText(block.explanation)
        : undefined

      return (
        <div className="my-5" data-content-block-id={block.id} key={block.id}>
          {question}
          {explanation && (
            <InputField
              disabled
              multiline
              state="success"
              value={explanation}
            />
          )}
          {block.criteria.length > 0 && (
            <div className="mt-3">
              <Text size="label" tone="muted">
                Grading criteria
              </Text>
              <ul className="marker:text-success my-1 list-disc ps-6">
                {block.criteria.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )
    }
    case 'sc':
    case 'mc': {
      const options = block.opts.map((opt) => getPlainText(opt))
      const correctIndexes =
        block.subtype === 'sc' ? [block.answer] : block.answer
      const correctOptions = correctIndexes.map((index) => options[index])
      const feedback = Object.fromEntries(
        correctOptions.map((option) => [option, 'success' as const]),
      )
      const explanation = block.explanation
        ? getPlainText(block.explanation)
        : undefined

      const field = (
        <ChoiceField
          aria-label={block.question ? getPlainText(block.question) : 'Answer'}
          disabled
          feedback={feedback}
          inline={block.inline}
          multiple={block.subtype === 'mc'}
          name={`question-${block.id}`}
          options={options}
          state="success"
          tip={block.inline ? undefined : explanation}
          value={correctOptions}
        />
      )

      if (block.inline) {
        return (
          <span
            className="inline-flex flex-wrap items-center"
            data-content-block-id={block.id}
            key={block.id}
          >
            {block.question && renderBlocks(block.question, context)}
            {field}
            {explanation && (
              <IconButton className="text-success" size="sm" variant="text">
                <MoreHorizontalIcon />
                {explanation}
              </IconButton>
            )}
          </span>
        )
      }

      return (
        <div className="my-5" data-content-block-id={block.id} key={block.id}>
          {question}
          {field}
        </div>
      )
    }
    case 'sr':
      return (
        <div
          className="my-5 flex flex-col gap-3"
          data-content-block-id={block.id}
          key={block.id}
        >
          {question}
          <Text size="label" tone="muted">
            Reference answer
          </Text>
          <AudioPlayer
            autoplay
            className="max-w-md"
            fullWidth
            minimalBelowMs={5_000}
            src={context.resolveFileUrl(block.answer.src)}
          />
          <Text>{block.answer.transcript}</Text>
        </div>
      )
  }
  return null
}

export function ClozeAnswerAudio({
  block,
  resolve,
}: {
  block: Extract<Block, { type: 'cloze' }>
  resolve: (src: string) => string
}) {
  return block.answerAudio ? (
    <AudioPlayer
      autoplay
      minimalBelowMs={5_000}
      src={resolve(block.answerAudio.src)}
    />
  ) : null
}

function DiffCodeBlock({
  block,
  context,
}: {
  block: Extract<Block, { type: 'pre' }>
  context: RenderContext
}) {
  const lines = block.children.filter(
    (child): child is Extract<Block, { type: 'diff' }> => child.type === 'diff',
  )
  const code = lines
    .map((line) => `${diffMarker(line.kind)}${getPlainText(line.children)}`)
    .join('\n')

  return (
    <div
      className="group text-on-surface relative my-5 min-w-0"
      data-content-block-id={block.id}
    >
      <CodeHighlightingControls
        allowLanguageSelection={false}
        code={code}
        language="diff"
      />
      <pre
        className={cn(
          codeHighlightingSurfaceClassName,
          'p-0 py-4 md:p-0 md:py-6',
        )}
        data-content-block-diff=""
      >
        <code className="font-inherit block min-w-max">
          {lines.map((line) => (
            <DiffContent
              block={line}
              className="block px-4 font-mono whitespace-pre no-underline md:px-6"
              key={line.id}
            >
              <span aria-hidden className="me-2 inline-block w-3 font-semibold">
                {diffMarker(line.kind)}
              </span>
              {renderBlocks(line.children, context)}
            </DiffContent>
          ))}
        </code>
      </pre>
    </div>
  )
}

function DiffContent({
  block,
  children,
  className,
}: {
  block: Extract<Block, { type: 'diff' }>
  children: ReactNode
  className?: string
}) {
  if (block.kind === 'add') {
    return (
      <ins
        className={cn('bg-surface-success no-underline', className)}
        data-diff-kind="add"
      >
        {children}
      </ins>
    )
  }
  if (block.kind === 'delete') {
    return (
      <del
        className={cn('bg-surface-error no-underline', className)}
        data-diff-kind="delete"
      >
        {children}
      </del>
    )
  }
  return (
    <span className={className} data-diff-kind="context">
      {children}
    </span>
  )
}

function ReviewContent({
  block,
  children,
}: {
  block: Extract<Block, { type: 'review' }>
  children: ReactNode
}) {
  const className = reviewPresentationClassName(block)
  const data = reviewPresentationDataAttributes(block)
  const structuralChildren = reviewStructuralChildren({
    block,
    children,
    className,
    data,
  })
  if (structuralChildren) return structuralChildren

  if (block.change === 'add') {
    return (
      <ins
        className={cn(block.display === 'block' && 'block', className)}
        {...data}
      >
        {children}
      </ins>
    )
  }
  if (block.change === 'delete') {
    return (
      <del
        className={cn(block.display === 'block' && 'block', className)}
        {...data}
      >
        {children}
      </del>
    )
  }
  if (block.display === 'block') {
    return (
      <div className={className} {...data}>
        {children}
      </div>
    )
  }
  return (
    <span className={className} {...data}>
      {children}
    </span>
  )
}

interface ReviewElementProps extends ReviewPresentationDataAttributes {
  className?: string
}

function reviewStructuralChildren({
  block,
  children,
  className,
  data,
}: {
  block: Extract<Block, { type: 'review' }>
  children: ReactNode
  className: string
  data: ReviewPresentationDataAttributes
}): ReactNode | null {
  if (
    block.display !== 'block' ||
    block.children.length === 0 ||
    !block.children.every((child) => ['li', 'tr', 'td'].includes(child.type))
  ) {
    return null
  }
  const elements = Children.toArray(children)
  if (
    elements.length !== block.children.length ||
    !elements.every((child) => isValidElement<ReviewElementProps>(child))
  ) {
    return null
  }
  return elements.map((child) =>
    cloneElement(child, {
      ...data,
      className: cn(child.props.className, className),
    }),
  )
}

const MermaidDiagram = lazy(() =>
  import('../../mermaid/src/mermaid-viewer').then(module => ({ default: module.MermaidDiagram })),
)

function GraphCodeBlock({ blockId, code }: { blockId: string; code: string }) {
  return (
    <figure
      className="border-separator bg-surface-2 my-5 overflow-hidden rounded-2xl border"
      data-content-block-id={blockId}
    >
      <header className="border-separator flex h-12 items-center justify-between gap-4 border-b px-4">
        <span className="text-label-medium font-medium">Graph</span>
        <Dialog disablePointerDismissal>
          <DialogTrigger render={<IconButton size="sm" variant="text" />}>
            <FullscreenIcon />
            Fullscreen
          </DialogTrigger>
          <DialogPopup className="h-dvh w-screen max-w-none rounded-none">
            <header className="border-separator flex h-14 shrink-0 items-center justify-between gap-4 border-b px-4">
              <DialogTitle className="text-header-small">Graph</DialogTitle>
              <DialogClose render={<IconButton size="sm" variant="text" />}>
                <ExitFullscreenIcon />
                Exit fullscreen
              </DialogClose>
            </header>
            <Suspense fallback={null}>
              <WhiteboardGraphViewer
                className="flex-1"
                code={code}
                maxFitScale={1.75}
              />
            </Suspense>
          </DialogPopup>
        </Dialog>
      </header>
      <div
        className="h-96"
        data-graph-viewer
        data-testid="whiteboard-graph-viewer"
      >
        <Suspense fallback={null}>
          <WhiteboardGraphViewer code={code} />
        </Suspense>
      </div>
    </figure>
  )
}

function hasPageHeaderContent(meta: PageMeta) {
  return Boolean(meta.title?.trim()) || Boolean(meta.image) || Boolean(meta.icon ?? meta.emoji) || pageTags(meta.tags).length > 0
}

function PageHeader({
  meta,
  resolveFileUrl,
}: {
  meta: PageMeta
  resolveFileUrl: (src: string) => string
}) {
  const hasIcon = Boolean(meta.icon ?? meta.emoji)
  const tags = pageTags(meta.tags)
  if (!hasPageHeaderContent(meta)) return null

  return (
    <header data-content-block-id={meta.id}>
      {meta.image && (
        <div className="relative">
          <Image
            alt=""
            className="w-full rounded-none sm:rounded-b-[28px]"
            intrinsic
            src={resolveFileUrl(meta.image)}
          />
          {tags.length > 0 && (
            <PageTags
              className="absolute right-6 bottom-0 translate-y-1/2"
              tags={tags}
            />
          )}
          {hasIcon && (
            <PageIcon
              className="absolute bottom-0 left-[12%] z-1 ml-1 -translate-x-1/2 translate-y-1/2"
              meta={meta}
              resolveFileUrl={resolveFileUrl}
            />
          )}
        </div>
      )}
      <div className={cn('px-6', meta.image ? 'pt-16' : 'pt-10')}>
        {hasIcon && !meta.image && (
          <PageIcon meta={meta} resolveFileUrl={resolveFileUrl} />
        )}
        <div className="flex justify-center">
          <Text
            className={cn(
              'text-header-huge font-bolder text-center tracking-wider text-balance',
              hasIcon && !meta.image && 'mt-3',
            )}
            render={<h1 />}
            tone="default"
          >
            {meta.title}
          </Text>
        </div>
        {!meta.image && tags.length > 0 && (
          <div className="mt-4">
            <PageTags className="justify-center" tags={tags} />
          </div>
        )}
      </div>
    </header>
  )
}

function PageTags({
  className,
  tags,
}: {
  className?: string
  tags: [string, string][]
}) {
  return (
    <div className={cn('flex flex-wrap gap-3', className)}>
      {tags.map(([tag, color]) => (
        <Chip key={tag} tagColor={chipTagColor(color)} variant="tag">
          {tag}
        </Chip>
      ))}
    </div>
  )
}

function PageIcon({
  className,
  meta,
  resolveFileUrl,
}: {
  className?: string
  meta: PageMeta
  resolveFileUrl: (src: string) => string
}) {
  if (meta.emoji) {
    return (
      <Text
        aria-hidden
        className={cn('text-emoji-big', className)}
        inline
        render={<span />}
      >
        {meta.emoji}
      </Text>
    )
  }
  if (!meta.icon) return null
  return (
    <Avatar
      alt=""
      className={cn('rounded-2xl bg-transparent', className)}
      name={meta.title}
      size="2xl"
      src={resolveFileUrl(meta.icon)}
    />
  )
}

function PageLink({
  pageBlock = false,
  blockId,
  context,
  id,
  spaceId,
}: {
  pageBlock?: boolean
  blockId: string
  context: RenderContext
  spaceId?: string
  id: string
}) {
  const target = resolvePageRef({ pageId: id, spaceId }, context.spaceId)
  const page = context.resolvePage(target)

  if (pageBlock) return <a
    className={cn(sidebarMenuButtonVariants({ size: 'sm' }), 'text-on-surface min-w-0 rounded-lg px-1.5 no-underline')}
    data-content-block-id={blockId}
    href={`#space:${encodeURIComponent(target.spaceId)}/${encodeURIComponent(target.pageId)}`}
  ><FileTextIcon aria-hidden={true} /><span>{page.label}</span></a>

  return (
    <Button
      className="text-label-big h-8 w-full justify-start px-0 font-semibold no-underline [&>span>span]:justify-start"
      data-content-block-id={blockId}
      nativeButton={false}
      render={<a href={`#space:${encodeURIComponent(target.spaceId)}/${encodeURIComponent(target.pageId)}`} />}
      variant="text"
    >
      {page.emoji && <span aria-hidden>{page.emoji}</span>}
      {page.label}
    </Button>
  )
}

function defaultPageInfo(_ref: PageRef): BlockViewerPageInfo {
  return { label: 'Page unavailable' }
}

function isPageNavigationParagraph(
  block: Extract<Block, { type: 'p' }>,
): boolean {
  return (
    block.children.length === 1 &&
    (block.children[0].type === 'page' || block.children[0].type === 'pageLink')
  )
}

function isPageNavigationColumns(
  block: Extract<Block, { type: 'cols' }>,
): boolean {
  return (
    block.children.length > 0 &&
    block.children.every(
      (column) =>
        column.type === 'col' &&
        column.children.length > 0 &&
        column.children.every(
          (child) => child.type === 'page' || child.type === 'pageLink' ||
            (child.type === 'p' && isPageNavigationParagraph(child)),
        ),
    )
  )
}

function renderChars(block: Extract<Block, { type: 'chars' }>) {
  let child: ReactNode = getTextFromChars(block)
  const format = block.format ?? 0
  if (format & FormatFlag.CODE) {
    child = (
      <code className={`${viewerStyles.code} cb-inline-code bg-gray/10 text-gray rounded-md px-[3px] py-px text-label-medium font-semi leading-none`}>
        {child}
      </code>
    )
  }
  if (format & FormatFlag.BOLD) child = <strong>{child}</strong>
  if (format & FormatFlag.ITALIC) child = <em>{child}</em>
  if (format & FormatFlag.STRIKE) child = <del>{child}</del>
  if (format & FormatFlag.UNDERLINE) child = <u>{child}</u>
  return child
}

function getCodeSource(block: Extract<Block, { type: 'pre' }>): string {
  if (block.children.every(child => child.type === 'diff')) {
    return block.children
      .filter(line => line.kind !== 'delete')
      .map(line => getProposedCodeText(line))
      .join('\n')
  }
  return getPlainText(block.children)
}

function getProposedCodeText(block: Block): string {
  if (block.type === 'diff' && block.kind === 'delete') return ''
  if ('children' in block && Array.isArray(block.children))
    return block.children.map(child => getProposedCodeText(child)).join('')
  return getPlainText([block])
}

function getPlainText(items: Blocks): string {
  return items
    .map((block) => {
      if (block.type === 'text') return block.text
      if (block.type === 'chars') return getTextFromChars(block)
      if (block.type === 'br') return '\n'
      if ('children' in block && Array.isArray(block.children))
        return getPlainText(block.children as Blocks)
      return ''
    })
    .join('')
}

function mediaWidthStyle(width?: number | string): CSSProperties | undefined {
  if (!width) return undefined
  return {
    '--content-block-media-width': normalizeWidth(width),
  } as CSSProperties
}

function normalizeWidth(width: number | string): string {
  return typeof width === 'string' ? width : `${width}%`
}

function getYoutubeId(src: string): string | undefined {
  const match =
    /(?:youtu\.be\/|youtube\.com\/(?:embed\/|shorts\/|watch\?v=))([^?&#/]+)/.exec(
      src,
    )
  return match?.[1] ?? (/^[\w-]{6,}$/.test(src) ? src : undefined)
}

function emphasisColorClass(color: string): string {
  return relativeColorClasses[relativeColorName(color)]
}

function pageTags(tags: PageMeta['tags']): [string, string][] {
  if (!tags) return []
  if (Array.isArray(tags)) return tags
  return tags.split(',').flatMap((value) => {
    const [tag, color] = value.split(':').map((part) => part.trim())
    return tag ? [[tag, color || 'gray']] : []
  })
}

function chipTagColor(color: string): ChipTagColor {
  return relativeColorName(color)
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 ** 2) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / 1024 ** 2).toFixed(1)} MB`
}

function mentionLabel(block: Extract<Block, { type: 'mention' }>): string {
  if (block.subtype === 'date') return new Date(block.date).toLocaleDateString()
  return `@${block.uid}`
}

function alertIcon(color: string): ReactNode {
  if (color === 'success') return <CircleCheckIcon />
  if (color === 'warning') return <TriangleAlertIcon />
  if (color === 'error') return <CircleAlertIcon />
  return <InfoIcon />
}

function identity(value: string): string {
  return value
}

function AnchorLink({ href, ...props }: BlockViewerLinkProps) {
  return <a href={href} {...props} />
}

function hasBlockChildren(children: Blocks): boolean {
  return children.some((child) => blockChildrenTypes.has(child.type))
}

const blockChildrenTypes = new Set<Block['type']>([
  'alert',
  'audio',
  'cols',
  'file',
  'h',
  'hr',
  'img',
  'list',
  'page',
  'pageLink',
  'pre',
  'preview',
  'question',
  'quote',
  'table',
  'video',
])
